import React, { useState } from "react"
import styled from "styled-components"
import { ButtonBase, Modal } from "@mui/material"
import {
  AddCircleOutline,
  Close,
  KeyboardArrowDown,
  KeyboardArrowUp,
  LocalPrintshopOutlined,
  RemoveCircleOutline,
} from "@mui/icons-material"
import { theme } from "../../../theme"
import { DownloadIcon } from "../../../icons"
import { Page, Document } from "react-pdf/dist/esm/entry.webpack"
import { PDFDocumentProxy } from "pdfjs-dist"

export enum DocumentViewerSupportedTypes {
  pdf = "application/pdf",
  jpeg = "image/jpeg",
  png = "image/png",
  unsupported = "unsupported",
}

export type DocumentViewerProps = {
  documentBlob: Blob
  name: string
  open?: boolean
  onClose: () => void
  authorizedTypes?: DocumentViewerSupportedTypes[]
  fileName?: string
  unsupportedDocumentLabel?: string
}

export const DocumentViewer = (props: DocumentViewerProps) => {
  const {
    documentBlob,
    name,
    open = false,
    onClose,
    fileName,
    unsupportedDocumentLabel,
    authorizedTypes = [
      DocumentViewerSupportedTypes.pdf,
      DocumentViewerSupportedTypes.png,
      DocumentViewerSupportedTypes.jpeg,
    ],
  } = props

  const [modalController, setModalController] = useState(true)
  const [numPages, setNumPages] = useState(0)
  const [pageNumber, setPageNumber] = useState(1)
  const [scale, setScale] = useState(100)

  const getType = (type: DocumentViewerSupportedTypes, authorizedTypes: DocumentViewerSupportedTypes[]) => {
    if (!!authorizedTypes && authorizedTypes.indexOf(type) > -1) {
      return type
    } else {
      return DocumentViewerSupportedTypes.unsupported
    }
  }

  const handleClose = () => {
    setModalController(false)
    setScale(100)
    setPageNumber(1)
    onClose()

    const iframe = document.getElementById("pdfIframe")
    iframe?.parentNode?.removeChild(iframe)
  }

  const onDocumentLoadSuccess = (data: PDFDocumentProxy) => {
    setNumPages(data?._pdfInfo?.numPages)
    setPageNumber(1)
    setModalController(true)
  }

  const changePage = (offset: number) => {
    setPageNumber((prevPageNumber) => prevPageNumber + offset)
  }

  const previousPage = () => {
    changePage(-1)
  }

  const nextPage = () => {
    changePage(1)
  }

  const changeScale = (offset: number) => {
    setScale((prevScale) => prevScale + offset)
  }

  const zoomIn = () => {
    changeScale(20)
  }

  const zoomOut = () => {
    if (scale <= 20) return
    changeScale(-20)
  }

  const renderDocument = () => {
    const type: DocumentViewerSupportedTypes = getType(
      documentBlob.type as DocumentViewerSupportedTypes,
      authorizedTypes
    )
    switch (type) {
      case DocumentViewerSupportedTypes.pdf:
        return (
          <Document file={documentBlob} onLoadSuccess={onDocumentLoadSuccess}>
            <Page pageNumber={pageNumber} />
          </Document>
        )
      case DocumentViewerSupportedTypes.png:
      case DocumentViewerSupportedTypes.jpeg:
        return <img src={URL.createObjectURL(documentBlob)} alt={name} height="95" />
      case DocumentViewerSupportedTypes.unsupported:
      default:
        return <WrongTypeBox>{unsupportedDocumentLabel}</WrongTypeBox>
    }
  }

  const onDownloadClicked = () => {
    const blobUrl = URL.createObjectURL(documentBlob)

    const link = document.createElement("a")
    link.href = blobUrl
    link.download = fileName!

    document.body.appendChild(link)

    link.dispatchEvent(
      new MouseEvent("click", {
        bubbles: true,
        cancelable: true,
        view: window,
      })
    )

    document.body.removeChild(link)
  }

  const onPrintClicked = () => {
    const iframe = document.createElement("iframe")
    const blobUrl = URL.createObjectURL(documentBlob)
    document.body.appendChild(iframe)
    iframe.style.display = "none"
    iframe.id = "pdfIframe"
    iframe.src = blobUrl
    iframe.onload = () => {
      iframe.focus()
      iframe.contentWindow?.print()
    }
  }

  return (
    <Modal open={open} onClose={handleClose} data-cy="documentViewerDialog">
      <>
        <ModalHeader>
          <ModalClose>
            <ButtonBase onClick={handleClose}>
              <Close />
            </ButtonBase>
          </ModalClose>
          {name}
          <ModalAction>
            {!!fileName && (
              <ButtonBase onClick={onDownloadClicked}>
                <DownloadIcon />
              </ButtonBase>
            )}
            <ButtonBase onClick={onPrintClicked}>
              <LocalPrintshopOutlined />
            </ButtonBase>
          </ModalAction>
        </ModalHeader>

        <OuterDocumentWrapper>
          <InnerDocumentWrapper $scale={scale}>{renderDocument()}</InnerDocumentWrapper>
        </OuterDocumentWrapper>

        {modalController && (
          <ModalController>
            <ButtonBase onClick={previousPage} disabled={pageNumber <= 1}>
              <KeyboardArrowUp />
            </ButtonBase>
            <p>
              {pageNumber || (numPages ? 1 : "--")} / {numPages || "--"}
            </p>

            <ButtonBase onClick={nextPage} disabled={pageNumber >= numPages}>
              <KeyboardArrowDown />
            </ButtonBase>
            <ButtonBase onClick={zoomIn}>
              <AddCircleOutline />
            </ButtonBase>
            <ButtonBase onClick={zoomOut}>
              <RemoveCircleOutline />
            </ButtonBase>
          </ModalController>
        )}
      </>
    </Modal>
  )
}

const InnerDocumentWrapper = styled.div<{ $scale: number }>`
  canvas,
  img {
    height: 100% !important;
    margin: auto;
  }

  canvas {
    width: ${(p) => p.$scale}% !important;
  }

  img {
    width: calc(${(p) => p.$scale}% / 2) !important;
    display: block;
  }

  :has(canvas) {
    margin: ${(p) => (p.$scale <= 100 ? "auto" : 0)};
    width: 90%;
  }

  :has(img) {
    display: block;
    width: 100%;
  }
`

const OuterDocumentWrapper = styled.div`
  min-width: 370px;
  height: calc(100% - ${theme.ui.navbar.height});
  overflow: auto;
  width: 100%;

  .react-pdf__Page__textContent {
    display: none;
  }
`

const ModalHeader = styled.div`
  width: 100%;
  text-align: center;
  color: ${theme.color.white};
  display: flex;
  justify-content: space-between;
  padding: 22px;
  height: ${theme.ui.navbar.height};
  background-color: rgba(0, 0, 0, 0.5);
`

const ModalClose = styled.div`
  display: flex;
  justify-content: space-between;
  width: 57px;
`

const ModalAction = styled.div`
  display: flex;
  justify-content: flex-end;
  width: 57px;
  gap: ${theme.spacing.m};
`

const ModalController = styled.div`
  display: flex;
  justify-content: space-evenly;
  color: ${theme.color.white};
  background-color: ${theme.color.black5};
  border-radius: ${theme.borderRadius};
  width: 220px;
  margin: auto;
  position: absolute;
  left: 50%;
  bottom: 1%;
  -webkit-transform: translate(-50%, -50%);
  transform: translate(-50%, -50%);
`

const WrongTypeBox = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  -webkit-transform: translate(-50%, -50%);
  transform: translate(-50%, -50%);
  background-color: ${theme.color.white};
  border-radius: ${theme.borderRadius};
  padding: ${theme.spacing.xl} ${theme.spacing.xxl};
  width: 360px;
  text-align: center;
`
